import Application from "../../Application";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import {
  CaseLawDocumentStatus,
  ContentTypes,
  CourtCaseType
} from "../common/Constants";
import CreateAttachmentFileInteractor from "../interactor/attachments/CreateAttachmentFileInteractor";
import CreateAttachmentInteractor from "../interactor/attachments/CreateAttachmentInteractor";
import RemoveCaseLawDocumentAttachmentInteractor from "../interactor/attachments/RemoveCaseLawDocumentAttachment";
import FindCourtCaseDetailsInteractor from "../interactor/FindCourtCaseDetailsInteractor";
import PublishCaseLawDocumentInteractor from "../interactor/PublishCaseLawDocumentInteractor";
import RevertCaseLawDocumentStatusInteractor from "../interactor/RevertCaseLawDocumentStatusInteractor";
import UpdateCaseLawDocumentInteractor from "../interactor/UpdateCaseLawDocumentInteractor";
import { IAttachment } from "../model/Attachment";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import { ICaseLawDocument } from "../model/CaseLawDocument";

import { ICourtCase } from "../model/CourtCase";
import { IOrganisation } from "../../organisations/model/Organisation";
import AttachmentPresentationModel, {
  TAttachmentPresentationModel
} from "./model/AttachmentPresentationModel";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import CaseLawDocumentPresentationModel, {
  TCaseLawDocumentPresentationModel
} from "./model/CaseLawDocumentPresentationModel";
import { IUser } from "../../user/model/User";
import FindIndexesInCategoryForOrganisationInteractor from "../../organisations/interactors/FindIndexesInCategoryForOrganisationInteractor";
import ArchiveCaseLawDocumentInteractor from "../interactor/ArchiveCaseLawDocumentInteractor";
import { TSearchQueryRequest } from "../../search/service/SearchService";
import RunSimpleSearchQueryInteractor from "../../search/interactor/RunSimpleSearchQueryInteractor";
import { IInvalidatedCaseLawDocument } from "../model/InvalidatedCaseLawDocument";
import SummaryTranslation from "../../core/model/translations/SummaryTranslation";
import { TOrganisationIndexGroupPresentationModel } from "../../organisations/presenter/model/OrganisationIndexGroupPresentationModel";
import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import GetAllCaseLawDocumentAttachmentsInteractor from "../interactor/attachments/GetAllCaseLawDocumentAttachmentsInteractor";
import { IDocumentTypeIndexCategory } from "../../documentTypes/model/DocumentTypeIndexCategory";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import DocumentTypeIndexCategoryPresentationModel from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import { ICourtCaseConnectionType } from "../model/CourtCaseConnectionType";
import { TCourtCaseSelectionModel } from "./CaseLawDocumentCreationPresenter";
import ShowInfoMessageInteractor from "../../core/interactor/notifications/ShowInfoMessageInteractor";
import { TSearchCriteriaFilter } from "../../search/model/SearchCriteria";
import GetAllIndexTypeAttributesInteractor from "../../indexTypes/interactors/GetAllIndexTypeAttributesInteractor";
import { IIndexTypeAttribute } from "../../indexTypes/model/IndexTypeAttribute";
import IndexAttributeData, {
  IIndexAttributeData
} from "../../indexTypes/model/IndexAttributeData";
import CreateIndexAttributeDataInteractor from "../../indexTypes/interactors/CreateIndexAttributeDataInteractor";
import GetIndexAttributeDataByIndexInteractor from "../../indexTypes/interactors/GetIndexAttributeDataByIndexInteractor";
import ShowWarningMessageInteractor from "../../core/interactor/notifications/ShowWarningMessageInteractor";
import { ICaseDecision } from "../../caseDecisions/model/CaseDecision";

export interface TCaseLawDocumentUpdatingPresenter
  extends TLoadingAwarePresenter {
  caseLawDocument: TCaseLawDocumentPresentationModel;
  organisations: IOrganisation[];
  caseLawDocumentTypes: TDocumentType[];
  caseLawDocumentIndexes: TOrganisationIndexPresentationModel[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  caseLawDocumentAttachments: TAttachmentPresentationModel[];
  uploadedFile: any;
  indexHierarchyList: any[];
  connectedCourtCasesList: ICourtCase[];
  contentTypes;
  selectedOrganisationId: number;
  organisationIndexes: IDocumentTypeIndexCategory[];
  selectedCourtCases: ICourtCase[];
  selectedIndex: TOrganisationIndexPresentationModel[][];
  organisationCaseLawDocumentTypes: TDocumentType[];
  organisationCaseLawDocumentTypeId?: number;
  nonAnonymisedAttachmentsCount: number;
  user?: IUser;
  parentGroupList: TOrganisationIndexGroupPresentationModel[][];
  secondGroupList: TOrganisationIndexGroupPresentationModel[][];
  selectedParentGroupIdList: number[];
  selectedSecondGroupIdList: number[];
  invalidatedCaseLawDocuments: TCaseLawDocumentPresentationModel[];
  publishedCaseLawDocuments: any[];
  invalidatePublishedCaseLawDocsVisible: boolean;
  courtCaseSelectionResult?: ICourtCase;
  courtCaseConnectionTypeList: ICourtCaseConnectionType[];
  createCourtCasePopupVisible: boolean;
  createCourtCaseModalVisible: boolean;
  nonExistantCourtCaseNumber?: string;
  uploadedCourtCaseAttachment?: IAttachment;
  anonimisedUploadedFile: any;
  courtCaseTypes: IGenericCodebookItem[];
  decisionTypes: IGenericCodebookItem[];
  selectedIndexForAttribute?: IOrganisationIndex;
  indexTypeAttributes?: IIndexTypeAttribute[];
  indexAttributeDataList?: IIndexAttributeData[];
  indexDataModalVisible: boolean;
  previousSelectedIds: number[][];
  selectedCaseDecisions?: any;
}

export interface ICaseLawDocumentUpdatingPresenter
  extends TCaseLawDocumentUpdatingPresenter,
    TPresentable {
  loadCaseLawDocument(caseLawDocuments: ICaseLawDocument): void;
  loadReferenceValues(
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    organisationCaseLawDocumentTypes,
    contentTypes: IGenericCodebookItem[],
    invalidatedCaseLawDocuments: ICaseLawDocument[],
    courtCaseConnectionTypeList: ICourtCaseConnectionType[],
    courtCaseTypes: IGenericCodebookItem[],
    decisionTypes: IGenericCodebookItem[]
  ): void;
  loadAttachments(caseLawDocumentAttachments: IAttachment[]): void;
  loadCaseLawDocumentIndexes(caseLawDocumentIndexes: IOrganisationIndex[]);
  setOrganisationId(organisationId: number): void;
  createConnectedCourtCase(courtCase: ICourtCase): void;
  removeConnectedCourtCase(courtCaseNumber: string): void;
  createAttachment(attachment: any): void;
  updateAttachment(attachment: any): void;
  removeAttachment(id: number): void;
  setFileValue(file: any): void;
  setAnonymisedFileValue(file: any): void;
  publishCaseLawDocument(): void;
  archiveCaseLawDocument(): void;
  revertCaseLawDocumentStatus(): void;
  updateCaseLawDocument(caseLawDocument: ICaseLawDocument): void;
  showCaseLawDocumentDetails(id: number): void;
  setCaseLawDocumentTypeCode(caseLawDocumentTypeId: number): void;
  loadCaseLawDocumentTypeIndexes(
    organisationIndexes: IDocumentTypeIndexCategory[]
  ): void;
  setParentGroupId(selectedParentGroupId: number, index: number): void;
  setSecondGroupId(selectedSecondGroupId: number, index: number): void;
  onSearch(value: string): void;
  setInvalidatePublishedCaseLawDocuments(value: boolean): void;
  showCreateCourtCasePopupVisible(value: boolean): void;
  showCreateCourtCaseModalVisible(value: boolean): void;
  createCourtCase(courtCase: ICourtCase): void;
  createCourtCaseAttachment(file: any): any;
  updateCourtCaseDetailsModalVisibility(state: boolean): void;
  findCourtCaseDetails(courtCaseNumber: string): void;
  updateCourtCaseSelection(result: TCourtCaseSelectionModel): void;
  canEdit(organisationId: number): void;
  openIndexDataModal(
    index: IOrganisationIndex,
    selectedIds: number[],
    indexNr: number
  ): void;
  closeIndexDataModal(): void;
  setAttributesData(
    indexTypeAttribute: IIndexTypeAttribute,
    attributeData: string
  ): void;
  saveAttributeData(): void;
  onSelectConnectedCourtCaseDecision(
    courtCase: ICourtCase,
    value: ICaseDecision
  );
}
const defaultState: TCaseLawDocumentUpdatingPresenter = {
  caseLawDocument: {} as TCaseLawDocumentPresentationModel,
  previousSelectedIds: [],
  organisations: [],
  caseLawDocumentTypes: [],
  caseLawDocumentIndexes: [],
  attachmentTypes: [],
  attachmentContentTypes: [],
  caseLawDocumentAttachments: [],
  uploadedFile: undefined,
  indexHierarchyList: [],
  connectedCourtCasesList: [],
  contentTypes: [],
  selectedOrganisationId: NaN,
  organisationIndexes: [],
  selectedCourtCases: [],
  selectedIndex: [],
  organisationCaseLawDocumentTypes: [],
  nonAnonymisedAttachmentsCount: NaN,
  parentGroupList: [],
  secondGroupList: [],
  selectedParentGroupIdList: [],
  selectedSecondGroupIdList: [],
  invalidatedCaseLawDocuments: [],
  publishedCaseLawDocuments: [],
  invalidatePublishedCaseLawDocsVisible: false,
  courtCaseConnectionTypeList: [],
  createCourtCasePopupVisible: false,
  createCourtCaseModalVisible: false,
  anonimisedUploadedFile: undefined,
  courtCaseTypes: [],
  decisionTypes: [],
  indexAttributeDataList: [],
  indexDataModalVisible: false,
  selectedCaseDecisions: {}
};
const CaseLawDocumentUpdatingPresenter = withStore<
  ICaseLawDocumentUpdatingPresenter,
  TCaseLawDocumentUpdatingPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): ICaseLawDocumentUpdatingPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TCaseLawDocumentUpdatingPresenter>();

    loader.start("caseLawDocumentLoader");
    const loadCaseLawDocument = (item: ICaseLawDocument) => {
      const { organisations, caseLawDocumentTypes } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();

      const caseLawDocumentType = caseLawDocumentTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      const selectedLanguage = _store.getState<any>().selectedLanguage;
      const summary = item.summaryTranslations.find(
        (elem) => elem.languageId === selectedLanguage.id
      )?.summaryTranslation;

      const caseLawDocument = CaseLawDocumentPresentationModel(
        item,
        caseLawDocumentType,
        organisation,
        summary
      );

      _store.update({
        caseLawDocument,
        selectedCourtCases: caseLawDocument.courtCaseList,
        connectedCourtCasesList: caseLawDocument.courtCaseList.filter(
          (elem) => elem.type === CourtCaseType.CONNECTED
        )
      });
      loader.stop("caseLawDocumentLoader");
    };
    const loadReferenceValues = (
      organisations: IOrganisation[],
      attachmentTypesList: IAttachmentType[],
      caseLawDocumentTypes: IDocumentType[],
      contentTypes: IGenericCodebookItem[],
      invalidatedCaseLawDocumentList: ICaseLawDocument[],
      courtCaseConnectionTypeList: ICourtCaseConnectionType[],
      courtCaseTypes: IGenericCodebookItem[],
      decisionTypes: IGenericCodebookItem[]
    ) => {
      let attachmentTypes = (attachmentTypesList || []).map((item) => {
        const contentType = contentTypes?.find(
          (value) => value.id === item.attachmentContentTypeId
        );
        return AttachmentTypePresentationModel(item, contentType);
      });
      const user = _store.getState<any>().user;
      const invalidatedCaseLawDocuments = invalidatedCaseLawDocumentList.map(
        (item) => {
          const caseLawDocumentType = caseLawDocumentTypes?.find(
            (value) => value.id === item.documentTypeId
          );
          const organisation = organisations?.find(
            (value) => value.id === item.organisationId
          );
          return CaseLawDocumentPresentationModel(
            item,
            caseLawDocumentType,
            organisation
          );
        }
      );

      _store.update({
        caseLawDocumentTypes,
        organisations,
        attachmentTypes,
        invalidatedCaseLawDocuments,
        contentTypes,
        user,
        courtCaseConnectionTypeList,
        courtCaseTypes,
        decisionTypes
      });
    };
    const getCaseLawDocumentTypeIndexes = async (
      caseLawDocumentTypeCode: number
    ) => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      let organisationIndexes = await _application.container
        .resolve<FindIndexesInCategoryForOrganisationInteractor>(
          "findIndexesInCategoryForOrganisation"
        )
        .execute(caseLawDocumentTypeCode, caseLawDocument.organisationId);
      loadCaseLawDocumentTypeIndexes(organisationIndexes);
    };

    const loadCaseLawDocumentTypeIndexes = (
      organisationIndexes: IDocumentTypeIndexCategory[]
    ) => {
      const { selectedLanguage, caseLawDocumentIndexes } =
        _store.getState<any>();
      var selectedIndexByCategories: IOrganisationIndex[][] = [];
      organisationIndexes.forEach((item) => {
        const result = caseLawDocumentIndexes.filter((o) =>
          item.organisationIndexList?.some(({ id }) => o.id === id)
        );
        selectedIndexByCategories.push(result);
      });
      _store.update({
        organisationIndexes: organisationIndexes.map((item) =>
          DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
        ),
        selectedIndex: selectedIndexByCategories
      });
      let selectedIndexes = selectedIndexByCategories;
      let selectedParentGroupIdList: number[] = [];

      let selectedSecondGroupIdList: number[] = [];
      selectedSecondGroupIdList.length = selectedIndexes.length;
      selectedIndexes.forEach((selectedIndex, index) => {
        selectedIndex.forEach((element) => {
          const selectedSecondGroupId = element?.groups.find(
            (group) => group.parentId === selectedParentGroupIdList[index]
          )?.id;
          selectedSecondGroupIdList[index] = selectedSecondGroupId ?? NaN;
        });
      });

      let parentGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
      let secondGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
      organisationIndexes.forEach((item) => {
        let parentList: TOrganisationIndexGroupPresentationModel[] = [];
        let secondList: TOrganisationIndexGroupPresentationModel[] = [];
        let indexList = item.organisationIndexList?.map((element) =>
          OrganisationIndexPresentationModel(
            element,
            undefined,
            undefined,
            undefined,
            undefined,
            selectedLanguage
          )
        );
        indexList?.forEach((index) => {
          var parentGroup = index.groups.find(
            (group) => group.parentId == null
          );
          var secondGroup = index.groups.find(
            (group) => group.parentId === parentGroup?.id
          );
          if (
            parentGroup &&
            parentList.findIndex(
              (element) => element.id === parentGroup?.id
            ) === -1
          ) {
            parentList?.push(parentGroup);
          }

          if (
            secondGroup &&
            secondList.findIndex(
              (element) => element.id === secondGroup?.id
            ) === -1
          ) {
            secondList.push(secondGroup);
          }
        });
        parentGroupList.push(parentList);
        secondGroupList.push(secondList);
      });
      _store.update({
        organisationIndexes,
        parentGroupList,
        secondGroupList,
        selectedParentGroupIdList,
        selectedSecondGroupIdList
      });
    };
    const loadCaseLawDocumentIndexes = (
      caseLawDocumentIndexes: IOrganisationIndex[]
    ) => {
      const { selectedLanguage } = _store.getState<any>();
      var data = caseLawDocumentIndexes.map((item) =>
        OrganisationIndexPresentationModel(
          item,
          undefined,
          undefined,
          undefined,
          undefined,
          selectedLanguage
        )
      );
      _store.update({
        caseLawDocumentIndexes: data,
        selectedIndex: data
      });
    };
    loader.start("attachmentsLoader");
    const loadAttachments = (attachments: IAttachment[]) => {
      const { attachmentTypes } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      let caseLawDocumentAttachments = (attachments || []).map((item) => {
        const attachmentType = attachmentTypes?.find(
          (value) => value.id === item.attachmentTypeId
        );
        return AttachmentPresentationModel(item, attachmentType);
      });
      _store.update({
        caseLawDocumentAttachments
      });
      loader.stop("attachmentsLoader");
    };

    const setOrganisationId = async (organisationId: number) => {
      _store.update({ selectedOrganisationId: organisationId });
    };
    const findCourtCaseDetails = async (courtCaseNumber?: string) => {
      let { courtCaseSelectionResult, connectedCourtCasesList } =
        store.getState<TCaseLawDocumentUpdatingPresenter>();
      if (!courtCaseNumber || courtCaseNumber.trim() === "") {
        _store.update({
          courtCase: undefined,
          courtCaseSelectionResult: null,
          selectedCaseDecisions: {}
        });
        return false;
      }
      if (courtCaseSelectionResult?.courtCaseNumber === courtCaseNumber) {
        _store.update({
          courtCaseSelectionResult: courtCaseSelectionResult,
          courtCaseDetailsModalVisible: true
        });
        return true;
      }

      var record = await _application.container
        .resolve<FindCourtCaseDetailsInteractor>("findCourtCaseDetails")
        .execute(courtCaseNumber);
      if (record.courtCaseNumber) {
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("editors.case_law_documents.primary_cases.success")
          );
        record.phases = record.phases.filter(
          (p) =>
            connectedCourtCasesList.some(
              (c) => c.courtCaseNumber === p.courtCaseNumber
            ) === false
        );
        _store.update({
          courtCaseSelectionResult: record,
          courtCase: record,
          courtCaseDetailsModalVisible: true
        });
      } else {
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(
            translate("editors.case_law_documents.connected_cases.not_found")
          );

        showCreateCourtCasePopupVisible(true);
        _store.update({
          courtCase: undefined,
          courtCaseSelectionResult: null,
          courtCaseDetailsModalVisible: false,
          nonExistantCourtCaseNumber: courtCaseNumber
        });
      }
      return record !== undefined ?? false;
    };
    const createConnectedCourtCase = async (courtCase: ICourtCase) => {
      const {
        connectedCourtCasesList,
        courtCaseSelectionResult,
        selectedCaseDecisions
      } = _store.getState<TCaseLawDocumentUpdatingPresenter>();

      courtCase.type = CourtCaseType.CONNECTED;

      if (courtCase.courtCaseNumber == null) {
        _store.update({
          connectedCourtCasesList: [...connectedCourtCasesList]
        });
        _application.container
          .resolve<ShowInfoMessageInteractor>("showInfoMessage")
          .execute(
            translate("editors.case_law_documents.connected_cases.not_found")
          );
      } else {
        if (courtCaseSelectionResult) {
          courtCaseSelectionResult.connectionTypeId =
            courtCase.connectionTypeId;
          courtCaseSelectionResult.type = CourtCaseType.CONNECTED;
          courtCaseSelectionResult.primaryDecisionId =
            selectedCaseDecisions?.[
              courtCaseSelectionResult.courtCaseNumber
            ]?.id;
        }
        _store.update({
          connectedCourtCasesList: [
            ...connectedCourtCasesList,
            courtCaseSelectionResult
          ]
        });
      }
      _store.update({ courtCaseSelectionResult: undefined });
    };
    const updateCourtCaseDetailsModalVisibility = (
      modalVisibilityState: boolean
    ) => {
      _store.update({ courtCaseDetailsModalVisible: modalVisibilityState });
    };
    const updateCourtCaseSelection = (result: any) => {
      _store.update({
        courtCaseSelectionResult: result
      });
    };
    const createCourtCaseAttachment = async (file: any) => {
      if (file.status !== "removed") {
        let attachment = {
          name: file.name,
          valid: true,
          anonymised: false
        } as IAttachment;
        let record = await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(attachment, file);

        _store.update({
          uploadedCourtCaseAttachment: record
        });
        loader.stop("attachmentsLoader");
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("editors.project.create_success_message"));
        return record;
      } else {
        _store.update({
          uploadedCourtCaseAttachment: undefined
        });
        return undefined;
      }
    };
    const createCourtCase = (courtCase: any) => {
      const { uploadedCourtCaseAttachment, caseLawDocument } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      courtCase.type = CourtCaseType.CONNECTED;
      if (courtCase?.courtCaseNumber) {
        courtCase.decisions = [
          {
            name: courtCase.caseDecisionName,
            attachmentId: uploadedCourtCaseAttachment?.id,
            organisationId: caseLawDocument.organisationId,
            decisionDate: courtCase.decisionDate,
            decisionTypeId: courtCase.decisionTypeId,
            valid: true
          }
        ];
        updateCourtCaseSelection(courtCase);
        courtCase.type === CourtCaseType.PRIMARY
          ? _store.update({
              courtCase: courtCase,
              courtCaseDetailsModalVisible: true,
              nonExistantCourtCaseNumber: undefined
            })
          : _store.update({
              courtCaseDetailsModalVisible: true,
              nonExistantCourtCaseNumber: undefined
            });
        return true;
      }
    };
    const showCreateCourtCasePopupVisible = (value: boolean) => {
      _store.update({ createCourtCasePopupVisible: value });
    };

    const showCreateCourtCaseModalVisible = (value: boolean) => {
      _store.update({ createCourtCaseModalVisible: value });
      value === false &&
        _store.update({ nonExistantCourtCaseNumber: undefined });
    };

    const removeConnectedCourtCase = async (courtCaseNumber: string) => {
      const { connectedCourtCasesList } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();

      var courtCases = connectedCourtCasesList.filter(
        (item) => item.courtCaseNumber !== courtCaseNumber
      );
      _store.update({
        connectedCourtCasesList: courtCases
      });
    };

    const createAttachment = async (attachment: any) => {
      const {
        uploadedFile,
        anonimisedUploadedFile,
        attachmentTypes,
        caseLawDocument,
        contentTypes
      } = _store.getState<TCaseLawDocumentUpdatingPresenter>();
      loader.start("attachmentsLoader");
      attachment.id =
        attachment.identificationCode === "Y" ? undefined : attachment.id;
      const attachmentType = attachmentTypes?.find(
        (value) => value.id === attachment.attachmentTypeId
      );
      attachment.file = uploadedFile;
      let contentType = contentTypes.find(
        (ct) => ct.id === attachmentType?.attachmentContentTypeId
      );
      if (
        contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
        attachment.file &&
        !attachment.file?.isDummy
      ) {
        if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
          attachment.anonymised = false;
          let createdAttachment = await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(attachment, attachment.file, caseLawDocument.id);
          createdAttachment.anonymised = true;
          await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(
              createdAttachment,
              anonimisedUploadedFile,
              caseLawDocument.id
            );
        } else {
          await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(attachment, attachment.file, caseLawDocument.id);
        }
      } else {
        await _application.container
          .resolve<CreateAttachmentInteractor>("createAttachment")
          .execute(attachment, caseLawDocument.id);
      }
      let attachments = await _application.container
        .resolve<GetAllCaseLawDocumentAttachmentsInteractor>(
          "getAllCaseLawDocumentAttachments"
        )
        .execute(caseLawDocument.id);
      loadAttachments(attachments);
      _store.update({
        uploadedFile: undefined,
        anonimisedUploadedFile: undefined
      });
      loader.stop("attachmentsLoader");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      return true;
    };
    const updateAttachment = async (attachment: any) => {
      const {
        uploadedFile,
        anonimisedUploadedFile,
        attachmentTypes,
        contentTypes,
        caseLawDocument
      } = _store.getState<TCaseLawDocumentUpdatingPresenter>();
      loader.start("attachmentsLoader");
      const attachmentType = attachmentTypes?.find(
        (value) => value.id === attachment.attachmentTypeId
      );
      attachment.file = uploadedFile;
      let contentType = contentTypes.find(
        (ct) => ct.id === attachmentType?.attachmentContentTypeId
      );
      if (
        contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
        attachment.file &&
        !attachment.file?.isDummy
      ) {
        let createdAttachment = await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(attachment, attachment.file, caseLawDocument.id);
        if (anonimisedUploadedFile) {
          createdAttachment.anonymised = true;

          await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(
              createdAttachment,
              anonimisedUploadedFile,
              caseLawDocument.id
            );
        }
      } else {
        let createdAttachment = await _application.container
          .resolve<CreateAttachmentInteractor>("createAttachment")
          .execute(attachment, caseLawDocument.id);
        if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
          createdAttachment.anonymised = true;
          await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(
              createdAttachment,
              anonimisedUploadedFile,
              caseLawDocument.id
            );
        }
      }
      let attachments = await _application.container
        .resolve<GetAllCaseLawDocumentAttachmentsInteractor>(
          "getAllCaseLawDocumentAttachments"
        )
        .execute(caseLawDocument.id);
      loadAttachments(attachments);

      _store.update({
        uploadedFile: undefined,
        anonimisedUploadedFile: undefined
      });
      setFileValue(undefined);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("attachmentsLoader");
      return true;
    };

    const setFileValue = (file: any) => {
      _store.update({ uploadedFile: file });
    };
    const setAnonymisedFileValue = (file: any) => {
      _store.update({ anonimisedUploadedFile: file });
    };
    const removeAttachment = async (id: number) => {
      const { caseLawDocumentAttachments, caseLawDocument } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      loader.start("attachmentsLoader");
      var attachments = caseLawDocumentAttachments.filter(
        (item) => item.id !== id
      );
      await _application.container
        .resolve<RemoveCaseLawDocumentAttachmentInteractor>(
          "removeCaseLawDocumentAttachment"
        )
        .execute(caseLawDocument.id, id);

      _store.update({ caseLawDocumentAttachments: attachments });
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("attachmentsLoader");
    };
    const publishCaseLawDocument = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();

      await _application.container
        .resolve<PublishCaseLawDocumentInteractor>("publishCaseLawDocument")
        .execute(caseLawDocument.id);
    };
    const archiveCaseLawDocument = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      caseLawDocument.caseLawDocDate = new Date(caseLawDocument.caseLawDocDate);
      await _application.container
        .resolve<ArchiveCaseLawDocumentInteractor>("archiveCaseLawDocument")
        .execute(caseLawDocument.id);
      showCaseLawDocumentDetails(caseLawDocument.id);
    };
    const revertCaseLawDocumentStatus = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();

      await _application.container
        .resolve<RevertCaseLawDocumentStatusInteractor>(
          "revertCaseLawDocumentStatus"
        )
        .execute(caseLawDocument.id);
    };

    const updateCaseLawDocument = async (caseLawDocumentItem: any) => {
      loader.start("caseLawDocumentLoader");
      let { connectedCourtCasesList, caseLawDocument, selectedCaseDecisions } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      let translations = caseLawDocumentItem.summaryTranslations.map((item) => {
        var element = SummaryTranslation(item);
        element.organisationId = caseLawDocumentItem.organisationId;
        return element;
      });
      caseLawDocumentItem.caseLawDocDate =
        caseLawDocumentItem.caseLawDocDate.toDate();

      if (translations[0].summaryTranslation !== caseLawDocument.summary) {
        caseLawDocumentItem.summary = translations[0].summaryTranslation;
      } else {
        caseLawDocumentItem.summary = caseLawDocument.summary;
      }

      caseLawDocumentItem.summaryTranslations = translations;
      let elementList =
        caseLawDocumentItem.organisationIndexList &&
        caseLawDocumentItem.organisationIndexList.length > 0
          ? caseLawDocumentItem.organisationIndexList.flat(1)
          : [];
      let invalidatedCaseLawDocuments: IInvalidatedCaseLawDocument = {
        caseLawDocumentIdList: caseLawDocumentItem.invalidatedCaseLawDocuments,
        invalidationReason: caseLawDocumentItem.invalidationReason
      };
      await _application.container
        .resolve<UpdateCaseLawDocumentInteractor>("updateCaseLawDocument")
        .execute(
          caseLawDocumentItem,
          elementList.filter((item) => item !== null && item !== undefined),
          connectedCourtCasesList,
          invalidatedCaseLawDocuments
        );

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      showCaseLawDocumentDetails(caseLawDocumentItem.id);
      loader.stop("caseLawDocumentLoader");
    };
    const showCaseLawDocumentDetails = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-law-documents/${id.toString()}/details`
      });
    };

    const setCaseLawDocumentTypeCode = async (
      caseLawDocumentTypeId: number
    ) => {
      await getCaseLawDocumentTypeIndexes(caseLawDocumentTypeId);
      _store.update({
        caseLawDocumentTypeCode: caseLawDocumentTypeId
      });
    };
    const setParentGroupId = async (
      selectedParentGroupId: number,
      index: number
    ) => {
      let { selectedParentGroupIdList } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      selectedParentGroupIdList[index] = selectedParentGroupId;
      _store.update({
        selectedParentGroupIdList
      });
    };
    const setSecondGroupId = async (
      selectedSecondGroupId: number,
      index: number
    ) => {
      let { selectedSecondGroupIdList } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      selectedSecondGroupIdList[index] = selectedSecondGroupId;
      _store.update({
        selectedSecondGroupIdList
      });
    };
    const setInvalidatePublishedCaseLawDocuments = async (value: boolean) => {
      _store.update({ invalidatePublishedCaseLawDocsVisible: value });
    };
    const onSearch = async (value: string) => {
      if (value.length >= 3) {
        let query: TSearchCriteriaFilter = {
          phrase: value
        };

        let _searchQuery: TSearchQueryRequest = {
          phrase: query.phrase ?? "",
          status: CaseLawDocumentStatus.PUBLISHED
        };
        _store.update({
          searchQuery: _searchQuery
        });

        let result = await _application.container
          .resolve<RunSimpleSearchQueryInteractor>("runSimpleSearchQuery")
          .execute(_searchQuery);

        result &&
          _store.update({
            publishedCaseLawDocuments: result.formattedResults
          });
      }
    };

    const canEdit = (organisationId) => {
      const { user } = _store.getState<TCaseLawDocumentUpdatingPresenter>();
      if (user) {
        return user.canEdit(organisationId);
      } else {
        return false;
      }
    };

    const openIndexDataModal = async (
      index: IOrganisationIndex,
      selectedIds: number[],
      indexNr: number
    ) => {
      let { previousSelectedIds } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      let previousIds = previousSelectedIds?.[indexNr];
      if (previousIds && selectedIds.length < previousIds?.length) {
        previousSelectedIds[indexNr] = selectedIds;
        _store.update({
          previousSelectedIds
        });
        return;
      }
      if (index?.indexTypeId) {
        let indexTypeAttributes = await _application.container
          .resolve<GetAllIndexTypeAttributesInteractor>(
            "getAllIndexTypeAttributes"
          )
          .execute(index?.indexTypeId);
        let indexAttributeDataList = await _application.container
          .resolve<GetIndexAttributeDataByIndexInteractor>(
            "getIndexAttributeDataByIndex"
          )
          .execute(index?.id);
        let indexDataModalVisible = indexTypeAttributes.length > 0;
        previousSelectedIds[indexNr] = selectedIds;
        _store.update({
          indexTypeAttributes,
          indexAttributeDataList,
          indexDataModalVisible,
          selectedIndexForAttribute: index,
          previousSelectedIds
        });
      }
    };

    const closeIndexDataModal = () => {
      _store.update({
        indexDataModalVisible: false
      });
    };

    const setAttributesData = (
      indexTypeAttribute: IIndexTypeAttribute,
      attributeData: string
    ) => {
      loader.start("caseLawDocumentLoader");
      const { indexAttributeDataList, selectedIndexForAttribute } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      let indexAttributeData = {
        indexId: selectedIndexForAttribute?.id,
        indexTypeAttributeId: indexTypeAttribute?.id,
        attributeData: attributeData,
        valid: true
      };
      let model = IndexAttributeData(indexAttributeData);
      let existingAttributeData = indexAttributeDataList?.find(
        (item) => item.indexTypeAttributeId === model.indexTypeAttributeId
      );
      if (existingAttributeData) {
        let index = indexAttributeDataList?.indexOf(existingAttributeData);
        if (indexAttributeDataList && index !== undefined) {
          indexAttributeDataList[index].attributeData = attributeData;
        }
      } else {
        indexAttributeDataList?.push(model);
      }
      let filteredList = indexAttributeDataList?.filter(
        (d) => d.attributeData && d.attributeData !== ""
      );
      _store.update({
        indexAttributeData: model,
        indexAttributeDataList: filteredList
      });
      loader.stop("caseLawDocumentLoader");
    };

    const saveAttributeData = async () => {
      loader.start("caseLawDocumentLoader");
      const { indexAttributeDataList, indexTypeAttributes } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      let invalidTypes: IIndexTypeAttribute[] = [];
      indexTypeAttributes?.forEach(async (type) => {
        let existingData = indexAttributeDataList?.filter(
          (d) => d.indexTypeAttributeId === type.id && d.attributeData
        );
        if (existingData?.length === 0 && type.required) {
          invalidTypes.push(type);
        }
      });
      if (invalidTypes.length > 0) {
        await _application.container
          .resolve<ShowWarningMessageInteractor>("showWarningMessage")
          .execute(translate("editors.case_law_documents.required_data"));
        loader.stop("indexTypeLoader");
        return;
      }
      indexAttributeDataList &&
        (await _application.container
          .resolve<CreateIndexAttributeDataInteractor>(
            "createIndexAttributeData"
          )
          .execute(indexAttributeDataList));
      closeIndexDataModal();
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      _store.update({ indexAttributeDataList: [] });
      loader.stop("caseLawDocumentLoader");
    };

    const onSelectConnectedCourtCaseDecision = (
      courtCase: ICourtCase,
      value: ICaseDecision
    ) => {
      let { courtCaseSelectionResult, selectedCaseDecisions } =
        _store.getState<TCaseLawDocumentUpdatingPresenter>();
      if (value) {
        selectedCaseDecisions[courtCase.courtCaseNumber] = value;
        _store.update({
          selectedCaseDecisions
        });
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadCaseLawDocument,
      loadAttachments,
      loadCaseLawDocumentIndexes,
      loadReferenceValues,
      setOrganisationId,
      createConnectedCourtCase,
      removeConnectedCourtCase,
      createAttachment,
      updateAttachment,
      removeAttachment,
      setFileValue,
      publishCaseLawDocument,
      revertCaseLawDocumentStatus,
      updateCaseLawDocument,
      setCaseLawDocumentTypeCode,
      showCaseLawDocumentDetails,
      setParentGroupId,
      setSecondGroupId,
      loadCaseLawDocumentTypeIndexes,
      archiveCaseLawDocument,
      onSearch,
      setInvalidatePublishedCaseLawDocuments,
      showCreateCourtCasePopupVisible,
      showCreateCourtCaseModalVisible,
      createCourtCase,
      createCourtCaseAttachment,
      updateCourtCaseDetailsModalVisibility,
      updateCourtCaseSelection,
      findCourtCaseDetails,
      setAnonymisedFileValue,
      canEdit,
      setAttributesData,
      openIndexDataModal,
      closeIndexDataModal,
      saveAttributeData,
      onSelectConnectedCourtCaseDecision
    };
  },
  defaultState
);
export default CaseLawDocumentUpdatingPresenter;
